<p-toast [style]="{marginTop: '80px'}"></p-toast>
<p-confirmDialog header="Confirmation" icon="pi pi-exclamation-triangle"></p-confirmDialog>

<!-- Top-right close button — only visible when we're inside a MatDialog.
     (When accessed via router, matDialogRef is null and the button hides.) -->
@if (matDialogRef) {
  <button type="button" class="tc-dialog-close" (click)="closeDialog()"
          aria-label="Close tape chart">
    <i class="pi pi-times"></i>
  </button>
}

<!-- ============================================================= -->
<!--  SMART-PARSE CONTEXT BANNER (comm-tool flow)                    -->
<!--  Appears when tape chart was opened from an inbox message.     -->
<!-- ============================================================= -->
@if (quoteContext && !quoteContext.flexMode &&
     (quoteContext.checkInDate || quoteContext.resortLabel || quoteContext.propertyCode
      || quoteContext.bedrooms || quoteContext.guestName)) {
  <div class="qc-banner">
    <i class="pi pi-bolt qc-banner-icon"></i>
    <span class="qc-banner-label">From email:</span>
    @if (quoteContext.checkInDate && quoteContext.checkOutDate) {
      <span class="qc-banner-chip">
        <i class="pi pi-calendar"></i>
        {{ quoteContext.checkInDate  | date:'MMM d' }}
        – {{ quoteContext.checkOutDate | date:'MMM d' }}
      </span>
    }
    @if (quoteContext.resortLabel) {
      <span class="qc-banner-chip"><i class="pi pi-home"></i> {{ quoteContext.resortLabel }}</span>
    }
    @if (quoteContext.propertyCode && !quoteContext.resortLabel) {
      <span class="qc-banner-chip"><i class="pi pi-home"></i> {{ quoteContext.propertyCode }}</span>
    }
    @if (quoteContext.bedrooms) {
      <span class="qc-banner-chip"><i class="pi pi-th-large"></i> {{ quoteContext.bedrooms }}-bedroom</span>
    }
    <span class="qc-banner-chip">
      <i class="pi pi-users"></i>
      {{ quoteContext.numAdults }}A
      @if (quoteContext.numChildren > 0) {, {{ quoteContext.numChildren }}C}
    </span>
    @if (quoteContext.guestName) {
      <span class="qc-banner-chip qc-banner-guest">
        <i class="pi pi-user"></i> {{ quoteContext.guestName }}
      </span>
    }

    <!-- Filter status + toggle -->
    @if (contextTotalCount > 0 && (quoteContext.resortCode || quoteContext.bedrooms)) {
      <span class="qc-banner-filter">
        @if (contextFilterActive) {
          <i class="pi pi-filter-fill"></i>
          Showing {{ contextMatchedCount }} of {{ contextTotalCount }} matching
          <a href="javascript:void(0)" (click)="toggleContextFilter()">Show all</a>
        } @else if (contextMatchedCount > 0 && contextMatchedCount < contextTotalCount) {
          <i class="pi pi-filter"></i>
          Showing all {{ contextTotalCount }}
          <a href="javascript:void(0)" (click)="toggleContextFilter()">
            Filter to {{ contextMatchedCount }} match{{ contextMatchedCount === 1 ? '' : 'es' }}
          </a>
        } @else if (contextMatchedCount === 0) {
          <i class="pi pi-exclamation-triangle"></i>
          No properties match — showing all {{ contextTotalCount }}
        }
      </span>
    }
    <span class="qc-banner-note">Switched to Dates mode · search auto-run</span>
  </div>
}

<!-- ============================================================= -->
<!--  FLEX-DATE FINDER PANEL                                         -->
<!--  "Any N nights in August" style requests.                      -->
<!-- ============================================================= -->
@if (quoteContext?.flexMode) {
  <div class="flex-panel">
    <div class="flex-panel-head">
      <div class="flex-panel-title">
        <i class="pi pi-sync flex-panel-icon"></i>
        <strong>Flexible date request</strong>
        <span class="flex-panel-sum">
          @if (quoteContext.flexNumNights) {
            {{ quoteContext.flexNumNights }} nights
          } @else {
            Browse availability
          }
          between {{ quoteContext.flexRangeStart | date:'MMM d, y' }}
          and {{ quoteContext.flexRangeEnd   | date:'MMM d, y' }}
          @if (quoteContext.resortLabel) { at <b>{{ quoteContext.resortLabel }}</b> }
          @if (quoteContext.bedrooms) { · <b>{{ quoteContext.bedrooms }}-bedroom</b> }
        </span>
      </div>
    </div>

    <!-- Flex-date finder table — extracted to BestFitListComponent.
         Receives the full unfiltered tape chart data and quote context.
         Emits (windowPicked) so we can highlight the property row here. -->
    <app-best-fit-list
      [tapeChartData]="allTapeChartsByProperty.length ? allTapeChartsByProperty : tapeChartsByProperty"
      [quoteContextInput]="quoteContext"
      [flexNightsList]="flexSelectedNights"
      [flexSearchingInput]="flexSearching"
      [msgOrigin]="msgOrigin"
      [msgRequest]="msgRequest"
      (windowPicked)="onFlexWindowPicked($event)">
    </app-best-fit-list>

  </div>
}

@if (showSearchUI) {
  <app-search-template searchLabel="Search" [showSearchButton]=true [reset]=true [showLocations]=true
    [showDevelopments]=true [showSleeps]=true [showListingTypes]=true [showShortTermRentals]=true [showYieldMaxProperties]=false
    [showRateBuckets]=false [showBedrooms]=true [showSeasons]=false [showReservationStatuses]=false [showStartDate]=true
    [showEndDate]=true [showSaleDateStart]=false [showSaleDateEnd]=false [defaultStartDate]="defaultStartDate"
    [showSaleYear]=false [displayRecordsOnPageLoad]="false" [defaultEndDate]="defaultEndDate" [showViewTypes]=true
    [defaultShortTermRentalIds]="defaultShortTermRentalIds" [shortTermRentalId]="searchedShortTermRentalId"
    [startDate]="searchedStartDate" [endDate]="searchedEndDate" [defaultReservationStatuses]="defaultReservationStatuses"
    [showNumNts]="!!quoteContext?.flexMode"
    [preSetNumNts]="flexPreSetNights"
    (doSearchCB)="onSearchSubmit($event)">
  </app-search-template>
}

<p-table #pt [value]="tapeChartsByProperty" [columns]="scrollingColumns" scrollDirection="both" frozenWidth="400px"
  [lazy]="false" dataKey="propertyCode" selectionMode="single" [scrollable]="true" [scrollHeight]="innerHeight +'px'"
  [(selection)]="tapeChart" (onRowSelect)="onRowSelect($event)" [loading]="loading" sortMode="multiple"
  [multiSortMeta]="multiSortMeta" [ngStyle]="{'width': 'auto'}" [resizableColumns]="true" [autoLayout]="true"
  [responsive]="true">

  <ng-template pTemplate="caption">
    <div class="tapechart-table-header d-flex align-items-center justify-content-between">
      <div class="text-start justify-content-between align-items-center">
        <h1 style="text-align: left; font-size: 1.5rem;">Tape Chart</h1>
        <div class="tc-propcode-filter d-flex align-items-center gap-2 mt-2">
          <input
            type="text"
            class="tc-propcode-input"
            placeholder="Property code"
            [(ngModel)]="propertyCodeFilter"
            (keyup.enter)="applyPropertyCodeFilter()"
            aria-label="Filter by property code" />
          <p-button label="Go" styleClass="p-button-sm" (onClick)="applyPropertyCodeFilter()"></p-button>
          @if (propertyCodeFilter) {
            <a href="javascript:void(0)" class="tc-propcode-clear"
               (click)="clearPropertyCodeFilter()" title="Clear filter">
              <i class="pi pi-times"></i>
            </a>
          }
        </div>
      </div>

      @if (showPropertyQuoteBtn) {
        <div class="text-start justify-content-between align-items-center">
          <p-button label="Quote" class="p-button-success" (onClick)="onOpenPropertiesQuote()"></p-button>
        </div>
      }

      <div class="d-flex gap-6 align-items-center">
        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: lightgreen"></div>
          <div class="legend-text">Available</div>
        </div>

        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: lightgrey"></div>
          <div class="legend-text">Guest</div>
        </div>

        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: hotpink;"></div>
          <div class="legend-text">Owner</div>
        </div>

        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: lightpink;"></div>
          <div class="legend-text">Owner Guest</div>
        </div>

        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: darkgray;"></div>
          <div class="legend-text">Maintenance</div>
        </div>

        <div class="legend-container">
          <div class="legend-color" style="text-align: left; background-color: yellow;"></div>
          <div class="legend-text">Hold</div>
        </div>
      </div>

      <!-- Trip Planner link BETWEEN HOLD and LOS -->
      <div class="d-flex align-items-center justify-content-center dialog-link-wrap">
        <a
          href="javascript:void(0)"
          class="dialog-link"
          (click)="openTripPlanner()"
          >
          Trip Planner
        </a>
      </div>

      <!-- TOP RIGHT CONTROLS -->
      <div class="tapechart-top-right d-flex align-items-center gap-3">

        <!-- Rate view selector -->
        <div>
          <p-selectButton
            [options]="rateOptions"
            [(ngModel)]="selectedRateOption"
            optionLabel="label"
            optionValue="value"
            (onChange)="switchTapeChartView($event)"
            >
          </p-selectButton>
        </div>

        <!-- HOLD toggle -->
        <div class="d-flex align-items-center gap-2">
          <p-checkbox
            [(ngModel)]="showAvailableOnly"
            (onChange)="showAvailableOnlyChange()"
            [binary]="true"
          ></p-checkbox>
          <span>&nbsp;Show available only</span>
        </div>



        <!-- LOS selector -->
        @if (selectedRateOption == 'los') {
          <div class="d-flex align-items-center">
            <p-dropdown
              [options]="nightList"
              appendTo="body"
              [(ngModel)]="searchWithLosNights"
              (onChange)="onNumNightsChange($event.value)"
              styleClass="p-column-filter"
              placeholder="Nights"
              [showClear]="true"
              optionLabel="name"
              optionValue="value"
              >
            </p-dropdown>
          </div>
        }

      </div>
    </div>
  </ng-template>

  <ng-template pTemplate="header" let-columns>
    <tr class="leftFrozenColumn">
      <th rowspan="2" pFrozenColumn>
        <p-checkbox [(ngModel)]="selectAllProperties" (onChange)="onPropertySelectionAll()"
        [binary]="true"></p-checkbox>
      </th>

      @for (col of frozenColumns; track col) {
        <th rowspan="2" style="width:75px;height:115px;padding: .2em .2em !important;" pFrozenColumn>
          <div class="d-flex flex-column">
            <span (click)="sortResult(col.field)">{{col.header}}</span>
            <p-sortIcon field="col.field" (click)="sortResult(col.field)">
            </p-sortIcon>
          </div>
        </th>
      }

      @for (col of monthNameColumns; track col) {
        <th [attr.colspan]="col.days"
          [ngStyle]="{'width': 'auto','font-size' : '20px', 'color': '#3F51B5', 'text-align': 'align-items-center'}">
          {{col.month | date: 'MMMM y'}}
        </th>
      }
    </tr>

    <tr class="tape-chart-th">
      @for (col of columns; track col) {
        <th (click)="onAnalyzedDateClick(col.field)" [pSortableColumn]="col.field" [ngStyle]="{'width': 'auto'}">
          {{col.field | date: 'EEE'}}
          <br>
            {{col.field | date: 'dd'}}
          </th>
        }
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-rowData let-columns="columns">
      <tr class="ratetable tape-chart-th">
        <ng-container>
          <td pFrozenColumn>
            <p-checkbox [(ngModel)]="rowData.isSelected" (onChange)="onPropertySelection(rowData)"
            [binary]="true"></p-checkbox>
          </td>
        </ng-container>

        @for (col of frozenColumns; track col) {
          @if (col.field === 'propertyCode') {
          <td  [ngStyle]="{
            'background-color': rowData['listingStatus'] == '2' ? 'lightcoral' : ''
           }" style="width:75px;height:67px; padding: .2em .2em !important;" pFrozenColumn>
              <a [href]="'https://www.kbmresorts.com/vacation-rentals/' + rowData['shortTermRentalId']" target="_blank">
                {{ rowData[col.field] }}
              </a>
            </td>
          }
          @if (col.field !== 'propertyCode') {
            <td style="width:75px;height:67px; padding: .2em .2em !important;" pFrozenColumn>
              {{ rowData[col.field] }}
            </td>
          }
        }

        @for (rate of rowData.rates; track rate; let i = $index) {
          @if (rate.isDateMatched) {
            <td (click)="onAvailableCellClick(rowData, rate, $event)"
              [class.cell-picked-start]="rate.isPickedStart"
              [class.leftBorder]="rate.isStartDate && selectedRateOption == 'dates'"
              [class.rightBorder]="rate.isEndDate && selectedRateOption == 'dates'"
          [class.leftAndRightBorder]="rate.isStartDate && rate.isEndDate" [ngStyle]="rate.reservationId!=null?
					rate.reservationStatus=='H'?{'background-color':'yellow'}:
					rate.reservationType=='OG'?{'background-color':'lightpink'}:
					rate.reservationType=='M'?{'background-color':'darkgray'}:
					rate.reservationType=='O'?{'background-color':'hotpink'}:{'background-color':'lightgrey'}
					: {'background-color': 'lightgreen'}" >
              <div class="colfixdata">
                @if (!rate.reservationId) {
                  <div>
                    <div class="d-flex">
                      @if (rate.yieldPriceAMT) {
                        <div class="YieldedPrice" (click)="openNewReservationWindow(rowData.rates, i)">
                          @if (rate.showRateInformation && rate.rateAfterCouponAmt) {
                            <a style="color: '#0b78de'; text-decoration: line-through;" title="Dynamic Yielded Price AMT with {{rate.discountPCT * 100}}% {{rate.couponName}} coupon"
                              href="javascript:void(0);" class="YieldedPrice">{{calcCouponYielPrice(rate) | currency : 'USD' : 'symbol'
                            :'1.0-0'}}</a>
                          }
                          @if (rate.showRateInformation) {
                            <a style="color: '#0b78de'; display: block;" title="Dynamic Yielded Price AMT (After coupon discount if applied)"
                              href="javascript:void(0);" class="YieldedPrice">{{calcYielPrice(rate) | currency : 'USD' : 'symbol'
                            :'1.0-0'}}</a>
                          }
                          @if (!rate.showRateInformation) {
                            <a style="color: '#a30b2f'" title="Yielded Price AMT"
                              href="javascript:void(0);" class="YieldedPrice">{{calcYielPrice(rate) | currency : 'USD' : 'symbol'
                            :'1.0-0'}}</a>
                          }
                        </div>
                      }
                      @if (!rate.yieldPriceAMT && rate.rateYieldPriceAMT) {
                        <div class="YieldedPrice" title="Yielded Price AMT"
                          (click)="openNewReservationWindow(rowData.rates, i)">
                          <a href="javascript:void(0);" style="color: '#a30b2f'" class="YieldedPrice">{{calcYielPrice(rate) | currency :
                          'USD' : 'symbol' :'1.0-0'}}</a>
                        </div>
                      }
                      @if ((rate.showRateInformation && selectedRateOption == 'dates')
                        || (searchWithLosNights && searchWithLosNights != '0' && selectedRateOption == 'los' && rate.yieldPriceAMT)) {
                        <i class="pi pi-info-circle"
                        aria-hidden="true" style="color:blue; font-size:110%;" (click)="fetchRateLogOutput(rate, rowData.propertyCode)"></i>
                      }
                      <div title="List Price AMT">{{calcListPrice(rate) | currency : 'USD' : 'symbol' : '1.0-0'}}</div>
                    </div>
                  </div>
                }
                @if (rate.reservationId) {
                  <div>
                    <div class="d-flex">
                      <div style="color: '#a30b2f'" class="YieldedPrice" title="Yielded Price AMT">
                        {{rate.roomRatePN | currency : 'USD' : 'symbol' :
                        '1.0-0'}}
                      </div>
                      <div title="List Price AMT">{{calcListPrice(rate) | currency : 'USD' : 'symbol' : '1.0-0'}}</div>
                    </div>
                  </div>
                }
                @if (rate.reservationId) {
                  <div>
                    <div class="d-flex">
                      <div title="Check In Date"> </div>
                      @if (rate.maintenance) {
                        <div (click)="openMaintenanceProperty(rate)" class="maintenance-indicator"></div>
                      }
                      <div (click)="findReservationById(rate)"><a href="javascript:void(0);"
                      class="YieldedPrice">{{ rate.lastname?.trim() ? rate.lastname : rate.firstname }}</a></div>
                    </div>
                  </div>
                }
                @if (!rate.reservationId) {
                  <div>
                    <div class="d-flex">
                      <div title="Check In Date"> </div>
                      <div title="Quote"><a href="javascript:void(0);" (click)="openQuoteWindow(rowData.rates, i)"
                      class="YieldedPrice">Q</a></div>
                      @if (rate.maintenance) {
                        <div (click)="openMaintenanceProperty(rate)" class="maintenance-indicator"></div>
                      }
                    </div>
                  </div>
                }
              </div>
            </td>
          }
          @if (!rate.isDateMatched) {
            <td>No Rate</td>
          }
        }
      </tr>
    </ng-template>
  </p-table>

  <p-dialog header="Warning" [modal]="true" [(visible)]="showReservationWarning" [position]="position"
    [style]="{ width: '25rem' }">
    <span class="text-warning mb-5">
      <strong>{{warningMessage}}</strong>
    </span>
    <div class="flex justify-content-end gap-2">
      <p-button label="Cancel" severity="secondary" (onClick)="showReservationWarning = false"></p-button>
      <p-button label="Confirm & Continue" class="p-button-success" (onClick)="onConfirmReservationWarning()"></p-button>
    </div>
  </p-dialog>

  <!-- Quote Dialog -->
  <p-dialog class="tapechart-quote-popup" header="Quote" [modal]="true" [(visible)]="showQuoteWindow"
    [position]="position" [style]="{ width: '100rem', height:'100rem' }" [maximizable]="true">
    <form [formGroup]="form1">
      <div class="row">
        <div class="col-4">
          <span class="p-float-label">
            @if (form1.get('quoteNumNight').value == null) {
              <label for="quoteNumNight">Number of Nights</label>
            }
            <p-inputNumber inputId="integeronly" formControlName="quoteNumNight" id="quoteNumNight"
              (onInput)="onNumNightChange($event.value)">
            </p-inputNumber>
          </span>
        </div>
        <div class="col-4">
          <span class="p-float-label ">
            <p-calendar inputId="checkInDate" formControlName="checkInDate" [style]="{'width':'200px'}" appendTo="body"
              (onSelect)="onQuoteCheckInDateChange()">
            </p-calendar>
            <label for="checkInDate">CheckIn</label>
            @if (submitted && f.checkInDate.errors) {
              <div class="invalid-feedback">
                @if (f.checkInDate.errors.required) {
                  <div>{{validationMessages.checkInDate[0].message}}
                  </div>
                }
              </div>
            }
          </span>
        </div>
        <div class="col-4">
          <span class="p-float-label">
            <p-calendar inputId="checkOutDate" formControlName="checkOutDate" [style]="{'width':'200px'}" appendTo="body"
              (keyup)="compareDate($event)" (onSelect)="onQuoteCheckOutDateChange()" [minDate]="quoteMinCheckOutDate"
              [defaultDate]="quoteMinCheckOutDate">
            </p-calendar>
            <label for="checkOutDate">CheckOut</label>
            @if (isDateLessThanCheckinDate) {
              <div>
                <small class="p-error" style="background-color: white;">Must be greater than check in
                date</small>
              </div>
            }
            @if (!isDateLessThanCheckinDate && submitted && f.checkOutDate.errors) {
              <div class="invalid-feedback">
                @if (f.checkOutDate.errors.required) {
                  <div>{{validationMessages.checkOutDate[0].message}}
                  </div>
                }
              </div>
            }
          </span>
        </div>
        <div class="col-4">
          <span class="p-float-label">
            <p-dropdown inputId="dropdown1" [options]="adults" (keydown)="checkValidation('adults')"
              formControlName="numAdults" [ngClass]="{ 'is-invalid': submitted && f.numAdults.errors }" optionLabel="name"
              appendTo="body" optionValue="value">
            </p-dropdown>
            <label for="dropdown1">Adults</label>
            @if (submitted && f.numAdults.errors) {
              <div class="invalid-feedback">
                @if (f.numAdults.errors.required) {
                  <div>{{validationMessages.numAdults[0].message}}
                  </div>
                }
              </div>
            }
          </span>
        </div>
        <div class="col-4">
          <span class="p-float-label">
            <p-dropdown inputId="dropdown2" [options]="children" (keydown)="checkValidation('children')"
              formControlName="numChildren" [ngClass]="{ 'is-invalid': submitted && f.numChildren.errors }"
              optionLabel="name" appendTo="body" optionValue="value">
            </p-dropdown>
            <label for="dropdown2">Children</label>
            @if (submitted && f.numChildren.errors) {
              <div class="invalid-feedback">
                @if (f.numChildren.errors.required) {
                  <div>{{validationMessages.numChildren[0].message}}
                  </div>
                }
              </div>
            }
          </span>
        </div>
      </div>
      <div class="col-12 pl-0 text-right">
        @if (errorList.length > 0) {
          <div class="error-mess" [innerHTML]="errorMessages"></div>
        }
        @if (searchButtonLabel == ( false && !quote ) || ( false && quote )) {
          <a (click)="getQuoteForSearchRates(form1)"
            [disabled]="form1.invalid||formLoading" class="nav-btn btn-sm left-icon text-white me-0"
            style="background-color: #299A01 !important; background: #299A01 !important; border-radius: 4px !important; border-color: #299A01 !important; margin-left: auto;"><b>Search
          Rates</b>
        </a>
      }
      @if (searchButtonLabel == true) {
        <button [disabled]="form1.invalid||formLoading"
          class="nav-btn btn-sm left-icon me-2">
          <p-progressSpinner [style]="{width: '35px', height: '35px'}" styleClass="custom-spinner" strokeWidth="2"
          fill="#EEEEEE" animationDuration=".5s"></p-progressSpinner>
        </button>
      }
    </div>
    <div class="pl-2 pt-2">
      <b>Property :</b> {{quotePropertyCode}}
    </div>
    @if (formError) {
      <div>
        <br>
          @if (formError.message) {
            <table class="error-mess d-block">
              <tbody>
                <tr>
                  <td class="formError">{{formError.message}}</td>
                </tr>
              </tbody>
            </table>
          }
        </div>
      }
      @if (!formError && quote && quote.invoice) {
        <div>
          <app-reservation-invoice [quote]="quote" [showShortInvoiceDetail]="showShortInvoiceDetail"
            (updateQuote)="showCouponDialog()">
          </app-reservation-invoice>
          <br>
            @if (errorList) {
              <div style="padding:2px;">
                <table style="background-color:white;border:0px solid #ddd; width:100%; text-align: right;font-size: small;">
                  <tbody>
                    @for (error of errorList; track error) {
                      <tr>
                        <td style="padding:0px; color:red"><span [innerHtml]="error"></span></td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
            <table class="w-100">
              <tr>
                <td style="text-align: right;">
                  <input type="button" (click)="onBookNowClick()" mat-raised-button color="accent" [disabled]="formLoading"
                    value="Book Now" class="btn-blue-primary btn-green"
                    />
                  </td>
                </tr>
              </table>
            </div>
          }
        </form>
      </p-dialog>

      <!-- OpsNote Dialog -->
      <p-dialog header="Rate Details" [modal]="true" [(visible)]="showOpsNoteDialog"
        [style]="{ width: '100rem', height:'100rem' }" [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
        [maximizable]="true">
        <div class="p-4">

          <!-- Promotion Log button â€” live (reservation exists) or dry run (rate slot only) -->
          @if (selectedRateForOpsNotes?.shortTermRentalId) {
            <div class="mb-3">
              <a href="javascript:void(0);"
                (click)="openPromoLog(selectedRateForOpsNotes.reservationId, selectedRateForOpsNotes.shortTermRentalId)"
                style="color: #0b78de; font-weight: bold;">
                <i class="pi pi-list" style="margin-right: 5px;"></i>
                @if (selectedRateForOpsNotes.reservationId) {
                  <span>
                    View Promotion Log (Res# {{selectedRateForOpsNotes.reservationId}})
                  </span>
                }
                @if (!selectedRateForOpsNotes.reservationId) {
                  <span>
                    Simulate Promotion (Dry Run)
                  </span>
                }
              </a>
            </div>
          }
          @for (opsNoteDetail of opsNoteDetails; track opsNoteDetail) {
            <table>
              <tr>
                <div class="d-flex">
                  <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold; ">Property :</span>
                  <span>{{opsNoteDetail.propertyCode}}</span>
                </div>
              </tr>
              <tr>
                <div class="d-flex">
                  <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold; ">CheckIn :</span>
                  <span>{{opsNoteDetail.checkInDate != null ? opsNoteDetail.checkInDate : currentSearchForm.startDate | date:
                  'yyyy-MM-dd'}}</span>
                </div>
              </tr>
              <tr>
                <div class="d-flex">
                  <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">CheckOut :</span>
                  <span>{{opsNoteDetail.checkOutDate != null ? opsNoteDetail.checkOutDate : currentSearchForm.endDate | date:
                  'yyyy-MM-dd'}}</span>
                </div>
              </tr>
              <tr>
                <div class="d-flex">
                  <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">Nights :</span>
                  <span>{{opsNoteDetail.numNights}}</span>
                </div>
              </tr>
              <tr>
                <div class="d-flex">
                  <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">List Price :</span>
                  <span>{{opsNoteDetail.listPriceAMT | currency:'USD'}}</span><span>&nbsp; {{opsNoteDetail.listPriceAMT != null
                  ? '(' : ''}} {{opsNoteDetail.listPriceAMT | currency:'USD'}} {{opsNoteDetail.listPriceAMT != null ? '/' :
                  ''}} {{opsNoteDetail.numNights}} {{opsNoteDetail.listPriceAMT != null ? '=' : ''}}
                  {{(opsNoteDetail.listPriceAMT / opsNoteDetail.numNights) | currency:'USD'}} {{opsNoteDetail.listPriceAMT !=
                null ? ')' : ''}}</span>
              </div>
            </tr>
            <tr>
              <div class="d-flex">
                <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">Original Yield Price
                :</span>
                <span>{{opsNoteDetail.yieldPriceOrigAMT | currency:'USD'}}</span><span>&nbsp;
                {{opsNoteDetail.yieldPriceOrigAMT != null ? '(' : ''}} {{opsNoteDetail.yieldPriceOrigAMT | currency:'USD'}}
                {{opsNoteDetail.yieldPriceOrigAMT != null ? '/' : ''}} {{opsNoteDetail.numNights}}
                {{opsNoteDetail.yieldPriceOrigAMT != null ? '=' : ''}} {{(opsNoteDetail.yieldPriceOrigAMT /
              opsNoteDetail.numNights) | currency:'USD'}} {{opsNoteDetail.yieldPriceOrigAMT != null ? ')' : ''}}</span>
            </div>
          </tr>
          <tr>
            <div class="d-flex">
              <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">Final Yield Price
              :</span>
              <span>{{opsNoteDetail.yieldPriceAMT | currency:'USD'}}</span><span>&nbsp; {{opsNoteDetail.yieldPriceAMT !=
              null ? '(' : ''}} {{opsNoteDetail.yieldPriceAMT | currency:'USD'}} {{opsNoteDetail.yieldPriceAMT != null ?
              '/' : ''}} {{opsNoteDetail.numNights}} {{opsNoteDetail.yieldPriceAMT != null ? '=' : ''}}
              {{(opsNoteDetail.yieldPriceAMT / opsNoteDetail.numNights) | currency:'USD'}} {{opsNoteDetail.yieldPriceAMT
            != null ? ')' : ''}}</span>
          </div>
        </tr>
        <tr>
          <div class="d-flex">
            <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">Coupon
            :</span>
            {{selectedRateForOpsNotes?.couponName || 'No coupon applied'}}
            <span title="{{selectedRateForOpsNotes?.discountPCT != null ? 'Coupon Discount: ' + (selectedRateForOpsNotes?.discountPCT | percent) : ''}}">
              {{selectedRateForOpsNotes?.discountPCT != null ? ' (' : ''}}
              {{selectedRateForOpsNotes?.discountPCT | percent}}
              {{selectedRateForOpsNotes?.discountPCT != null ? ')' : ''}}
            </span>
            <span title="{{selectedRateForOpsNotes?.increaseLOSAmountPCT != null ? 'Increased PCT in LOS Amount: ' + (selectedRateForOpsNotes?.increaseLOSAmountPCT | percent) : ''}}">
              {{selectedRateForOpsNotes?.increaseLOSAmountPCT != null ? ' (' : ''}}
              {{selectedRateForOpsNotes?.increaseLOSAmountPCT | percent}}
              {{selectedRateForOpsNotes?.increaseLOSAmountPCT != null ? ')' : ''}}
            </span>
          </div>
        </tr>
        <tr>
          <div class="d-flex">
            <span style="min-width: 170px;padding-right:10px; text-align:left; font-weight: bold;">Coupon Amount
            :</span>
            <span>
              {{selectedRateForOpsNotes?.couponAmt | currency : 'USD' : 'symbol' : '1.0-0'}}
            </span>
          </div>
        </tr>
        <tr>
          <td style="min-width: 250px;padding-right:10px; padding-top: 10px; text-align:left; font-weight: bold;">
            <div class="d-flex gap-2">
              <a class="wiki-list-btn" href="javascript:void(0);"
              (click)="redirectToProperty(opsNoteDetail, 2)">Property</a>
              <a class="wiki-list-btn" href="javascript:void(0);" (click)="redirectToProperty(opsNoteDetail, 3)">Rate</a>
              <a class="wiki-list-btn" href="javascript:void(0);"
              (click)="redirectToProperty(opsNoteDetail, 4)">Seasons</a>
            </div>
          </td>
        </tr>
        <tr>
          <td colspan="2"><br><hr></td>
        </tr>
        @if (opsNoteDetail.logOutput) {
          <tr>
            <td colspan="2" style="white-space: pre-line;">
              <b style="font-size: 20px;">Yield Rate log Output</b>
              <br>
                <p-table #dt [value]="opsNoteDetail.logOutput" [responsiveLayout]="'scroll'" styleClass="p-datatable-striped"
                  [sortMode]="'multiple'" [style]="{ height: '500px' }" scrollHeight="500px">
                  <ng-template pTemplate="header">
                    <tr>
                      <th [pSortableColumn]="'logmanId'">Log ID
                        <p-sortIcon [field]="logmanId"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'analyzedDate'">Date
                        <p-sortIcon [field]="analyzedDate"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'eventName'">Event
                        <p-sortIcon [field]="eventName"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'inputValue'">Input Value
                        <p-sortIcon [field]="inputValue"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'inputValueName'">Input Value Name
                        <p-sortIcon [field]="inputValueName"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'changeValue'">Change Value
                        <p-sortIcon [field]="changeValue"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'changeAmount'">Change Amount
                        <p-sortIcon [field]="changeAmount"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'outputValue'">Output Value
                        <p-sortIcon [field]="outputValue"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'outputValueName'">Output Value Name
                        <p-sortIcon [field]="outputValueName"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'message'">Message
                        <p-sortIcon [field]="message"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'methodName'">Method
                        <p-sortIcon [field]="methodName"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'referenceId'">Reference ID
                        <p-sortIcon [field]="referenceId"></p-sortIcon>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-log let-rowIndex="rowIndex">
                    @if (!log.isBlankRow) {
                      <tr >
                        <td>{{ log.logmanId }}</td>
                        <td>{{ log.analyzedDate }}</td>
                        <td>{{ log.eventName }}</td>
                        <td>{{ log.inputValue | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                        <td>{{ log.inputValueName }}</td>
                        <td>{{ log.changeValue }}</td>
                        <td>{{ log.changeAmount | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                <td [ngClass]="{
                  'red-background': log.rateLogSeverity === 'RU',
                  'green-background': log.rateLogSeverity === 'RD',
                  'blue-background': log.rateLogSeverity === 'NA'
                }">{{ log.outputValue | currency : 'USD' : 'symbol' : '1.0-0'}}</td>
                        <td>{{ log.outputValueName }}</td>
                        <td>{{ log.message }}</td>
                        <td>{{ log.methodName }}</td>
                        <td>{{ log.referenceId }}</td>
                      </tr>
                    }
                    @if (log.isBlankRow) {
                      <tr class="blank-row">
                        <td colspan="12"></td>
                      </tr>
                    }
                  </ng-template>
                </p-table>
              </td>
            </tr>
          }
          <br>
            @if (opsNoteDetail.opsNotes) {
              <tr>
                <td colspan="2" style="white-space: pre-line;">
                  <b style="font-size: 20px;">Dynamic Rate log Output</b>
                  <br>
                    <p-table #dt [value]="opsNoteDetail.opsNotes" [responsiveLayout]="'scroll'" styleClass="p-datatable-striped"
                      [sortMode]="'multiple'" [style]="{ height: '500px' }" scrollHeight="500px">
                      <ng-template pTemplate="header">
                        <tr>
                          <th [pSortableColumn]="'logmanId'">Log ID
                            <p-sortIcon [field]="logmanId"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'analyzedDate'">Date
                            <p-sortIcon [field]="analyzedDate"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'eventName'">Event
                            <p-sortIcon [field]="eventName"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'inputValue'">Input Value
                            <p-sortIcon [field]="inputValue"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'inputValueName'">Input Value Name
                            <p-sortIcon [field]="inputValueName"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'changeValue'">Change Value
                            <p-sortIcon [field]="changeValue"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'changeAmount'">Change Amount
                            <p-sortIcon [field]="changeAmount"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'outputValue'">Output Value
                            <p-sortIcon [field]="outputValue"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'outputValueName'">Output Value Name
                            <p-sortIcon [field]="outputValueName"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'message'">Message
                            <p-sortIcon [field]="message"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'methodName'">Method
                            <p-sortIcon [field]="methodName"></p-sortIcon>
                          </th>
                          <th [pSortableColumn]="'referenceId'">Reference ID
                            <p-sortIcon [field]="referenceId"></p-sortIcon>
                          </th>
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-log let-rowIndex="rowIndex">
                        @if (!log.isBlankRow) {
                          <tr >
                            <td>{{ log.logmanId }}</td>
                            <td>{{ log.analyzedDate }}</td>
                            <td>{{ log.eventName }}</td>
                            <td>{{ log.inputValue | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                            <td>{{ log.inputValueName }}</td>
                            <td>{{ log.changeValue }}</td>
                            <td>{{ log.changeAmount | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                <td [ngClass]="{
                  'red-background': log.rateLogSeverity === 'RU',
                  'green-background': log.rateLogSeverity === 'RD',
                  'blue-background': log.rateLogSeverity === 'NA'
                }">{{ log.outputValue | currency : 'USD' : 'symbol' : '1.0-0'}}</td>
                            <td>{{ log.outputValueName }}</td>
                            <td>{{ log.message }}</td>
                            <td>{{ log.methodName }}</td>
                            <td>{{ log.referenceId }}</td>
                          </tr>
                        }
                        @if (log.isBlankRow) {
                          <tr class="blank-row">
                            <td colspan="12"></td>
                          </tr>
                        }
                      </ng-template>
                    </p-table>
                  </td>
                </tr>
              }
              @if (!opsNoteDetail.opsNotes) {
                <tr>
                  <td colspan="2" style="white-space: pre-line;">
                    <pre>Dynamic pricing adjustments not available</pre>
                  </td>
                </tr>
              }
              <tr>
                <td colspan="2">
                  <a href="javascript:void(0);" (click)="fetchCurrentRateQuoteWithAdjustments(opsNoteDetail)">
                    Fetch Detailed Adjustments
                  </a>
                </td>
              </tr>
            </table>
          }

          <table class="mt-6">
            <tr>
              <td colspan="2" style="white-space: pre-line;">
                <b style="font-size: 20px;">Add To Rate</b>
                <br><br>
                <p-table #dt [value]="rateFeesDetails" [responsiveLayout]="'scroll'" styleClass="p-datatable-striped"
                  [sortMode]="'multiple'" [style]="{ height: '500px' }" scrollHeight="500px">
                  <ng-template pTemplate="header">
                    <tr>
                      <th [pSortableColumn]="'orderProductId'">Product Id
                        <p-sortIcon [field]="orderProductId"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'name'">Name
                        <p-sortIcon [field]="name"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'listPrice'">List Price
                        <p-sortIcon [field]="listPrice"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'quantity'">Qty
                        <p-sortIcon [field]="quantity"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'subTotal'">Sub Total
                        <p-sortIcon [field]="subTotal"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'tax'">Tax
                        <p-sortIcon [field]="tax"></p-sortIcon>
                      </th>
                      <th [pSortableColumn]="'total'">Total
                        <p-sortIcon [field]="total"></p-sortIcon>
                      </th>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-log let-rowIndex="rowIndex">
                    <tr>
                      <td>{{ log.orderProductId }}</td>
                      <td>{{ log.name }}</td>
                      <td>{{ log.listPrice | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                      <td>{{ log.quantity }}</td>
                      <td>{{ log.subTotal | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                      <td>{{ log.tax | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                      <td>{{ log.total | currency : 'USD' : 'symbol' : '1.0-0'}} </td>
                    </tr>
                  </ng-template>
                </p-table>
              </td>
            </tr>
          </table>
        </div>
      </p-dialog>

      <!-- Promotion Log Dialog -->
      <p-dialog [header]="'Promotion Log â€” ' + promoLogReservationId" [modal]="true"
        [(visible)]="showPromoLogDialog"
        [style]="{ width: '90rem' }" [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
        [maximizable]="true">
        <div class="p-3">
          @if (promoLogLoading) {
            <div style="text-align:center; padding: 20px;">
              <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i> Loading...
            </div>
          }
          @if (!promoLogLoading && promoLogAlerts.length === 0) {
            <div style="color: grey; padding: 10px;">
              No promotion log entries found for this reservation.
            </div>
          }
          @if (!promoLogLoading && promoLogAlerts.length > 0) {
            <p-table
              [value]="promoLogAlerts" [scrollable]="true" scrollHeight="600px"
              styleClass="p-datatable-striped p-datatable-sm">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width: 80px;">Severity</th>
                  <th>Message</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-alert>
                <tr>
                  <td style="width: 80px;">
            <span [ngStyle]="{
              'color': alert.severity === 'ERROR'   ? '#c0392b' :
                       alert.severity === 'WARN'    ? '#c9a800' :
                       alert.severity === 'SUCCESS' ? '#27ae60' : '#2980b9',
              'font-weight': 'bold',
              'font-size': '12px'
            }">{{ alert.severity || 'INFO' }}</span>
                  </td>
                  <td style="white-space: pre-wrap; word-break: break-word; font-family: monospace; font-size: 13px;">
                    {{ alert.alertText || alert.errorMessage }}
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </div>
      </p-dialog>

      <!-- Coupon Dialog -->
      <p-dialog header="Coupon" [(visible)]="showCoupon" [style]="{width: 'auto', height: 'auto'}">
        @if (isSearching == true) {
          <div style="text-align: center;">
            <p-progressSpinner [style]="{width: '50px', height: '50px'}" styleClass="custom-spinner" strokeWidth="8"
            fill="var(--surface-ground)" animationDuration=".5s"></p-progressSpinner>
          </div>
        }
        @if (couponCodeSuccessMsg) {
          <div>
            <span style="background-color: white;color: green;">Coupon code amount {{quote?.couponDiscount}} applied
            successfully</span>
          </div>
        }
        @if (!couponCodeSuccessMsg) {
          <div>
            <input pautofocus="" [(ngModel)]="couponCode" aria-autocomplete="list" role="searchbox"
              class="p-element ng-tns-c118-14 p-autocomplete-input p-inputtext p-component ng-star-inserted" autocomplete="off"
              type="text" id="autocomplete" placeholder="Enter coupon code">
              &nbsp;&nbsp;
              <a class="nav-btn btn-sm left-icon text-white me-0" (click)="applyCouponCode()"
                style="background-color: #E18131 !important; color: #fff !important;"><b>Apply
              </b></a><br>
              @if (isCouponCodeIsApply) {
                <div>
                  <small class="p-error" style="background-color: white;">Please enter coupon code</small>
                </div>
              }
              @if (!quote?.couponApplied && invalidCouponCode) {
                <div>
                  <small class="p-error" style="background-color: white;">Coupon code is invalid or no longer
                  available</small>
                </div>
              }
            </div>
          }
        </p-dialog>

        <!-- TripPlanner -->
        <p-dialog
          header="KBM AI Trip Planner"
          [(visible)]="tripPlannerVisible"
          [modal]="true"
          [dismissableMask]="true"
          [draggable]="false"
          [resizable]="false"
          [style]="{ width: '85vw', height: '85vh' }"
  [contentStyle]="{
    height: 'calc(85vh - 60px)',
    padding: '0',
    overflow: 'hidden'
  }"
          >
          <!--<ai-dialog [aiAgentId]="7"></ai-dialog>-->
        </p-dialog>
